import { bootstrapApplication } from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import {
  AbstractSecurityStorage,
  authInterceptor,
  LogLevel,
  OidcSecurityService,
  provideAuth,
} from 'angular-auth-oidc-client';
import { LocalStorageService } from './app/core/services/local-storage.service';
import { catchError, switchMap } from 'rxjs/operators';
import { firstValueFrom, NEVER, of } from 'rxjs';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { apiInterceptor } from './app/core/interceptors/api.interceptor';
import { loadAppConfig } from './app/core/services/app-config.service';

// Tyler Forge Component Registration
import { defineComponents, IconRegistry } from '@tylertech/forge';
import {
  tylIconDashboard,
  tylIconShoppingCart,
  tylIconDescription,
  tylIconNotifications,
  tylIconNotificationsActive,
  tylIconNotificationsNone,
  tylIconPerson,
  tylIconLogout,
  tylIconAdd,
  tylIconEdit,
  tylIconDelete,
  tylIconSearch,
  tylIconClose,
  tylIconCheck,
  tylIconCheckCircle,
  tylIconError,
  tylIconErrorOutline,
  tylIconInfo,
  tylIconWarning,
  tylIconArrowBack,
  tylIconArrowForward,
  tylIconArrowDropDown,
  tylIconExpandMore,
  tylIconExpandLess,
  tylIconMoreVert,
  tylIconRefresh,
  tylIconFilterList,
  tylIconSort,
  tylIconDownload,
  tylIconUpload,
  tylIconVisibility,
  tylIconVisibilityOff,
  tylIconContentCopy,
  tylIconPrint,
  tylIconLock,
  tylIconBlock,
  tylIconSwapHoriz,
  tylIconStar,
  tylIconBusiness,
  tylIconLocationOn,
  tylIconSend,
  tylIconCancel,
  tylIconLocalShipping,
  tylIconInventory,
  tylIconDoneAll,
  tylIconAccountCircle,
  tylIconReceipt,
  tylIconReceiptLong,
  tylIconAssignment,
  tylIconArticle,
  tylIconInbox,
  tylIconPending,
  tylIconFactory,
  tylIconPackage,
  tylIconAttachMoney,
  tylIconPersonRemove,
  tylIconPersonAdd,
  tylIconAddLocation,
  tylIconPhone,
  tylIconEmail,
  tylIconLocationOff,
  tylIconHome,
  tylIconAddShoppingCart,
  tylIconRequestQuote,
  tylIconFactCheck,
  tylIconGridView,
  tylIconApps,
  tylIconList,
  tylIconViewCompact,
  tylIconViewAgenda,
  tylIconNoteAdd,
  tylIconNoteEdit,
  tylIconPayments,
} from '@tylertech/tyler-icons';

defineComponents();

IconRegistry.define([
  tylIconDashboard,
  tylIconShoppingCart,
  tylIconDescription,
  tylIconNotifications,
  tylIconNotificationsActive,
  tylIconNotificationsNone,
  tylIconPerson,
  tylIconLogout,
  tylIconAdd,
  tylIconEdit,
  tylIconDelete,
  tylIconSearch,
  tylIconClose,
  tylIconCheck,
  tylIconCheckCircle,
  tylIconError,
  tylIconErrorOutline,
  tylIconInfo,
  tylIconWarning,
  tylIconArrowBack,
  tylIconArrowForward,
  tylIconArrowDropDown,
  tylIconExpandMore,
  tylIconExpandLess,
  tylIconMoreVert,
  tylIconRefresh,
  tylIconFilterList,
  tylIconSort,
  tylIconDownload,
  tylIconUpload,
  tylIconVisibility,
  tylIconVisibilityOff,
  tylIconContentCopy,
  tylIconPrint,
  tylIconLock,
  tylIconBlock,
  tylIconSwapHoriz,
  tylIconStar,
  tylIconBusiness,
  tylIconLocationOn,
  tylIconSend,
  tylIconCancel,
  tylIconLocalShipping,
  tylIconInventory,
  tylIconDoneAll,
  tylIconAccountCircle,
  tylIconReceipt,
  tylIconReceiptLong,
  tylIconAssignment,
  tylIconArticle,
  tylIconInbox,
  tylIconPending,
  tylIconFactory,
  tylIconPackage,
  tylIconAttachMoney,
  tylIconPersonRemove,
  tylIconPersonAdd,
  tylIconAddLocation,
  tylIconPhone,
  tylIconEmail,
  tylIconLocationOff,
  tylIconHome,
  tylIconAddShoppingCart,
  tylIconRequestQuote,
  tylIconFactCheck,
  tylIconGridView,
  tylIconApps,
  tylIconList,
  tylIconViewCompact,
  tylIconViewAgenda,
  tylIconNoteAdd,
  tylIconNoteEdit,
  tylIconPayments,
]);

loadAppConfig()
  .then((appConfig) => {
    bootstrapApplication(AppComponent, {
      providers: [
        provideRouter(
          routes,
          withComponentInputBinding(),
          withInMemoryScrolling({
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled',
          }),
          withViewTransitions({
            skipInitialTransition: true,
          }),
        ),
        provideHttpClient(withXhr(), withInterceptors([authInterceptor(), apiInterceptor])),
        provideAnimations(),
        provideZonelessChangeDetection(),
        provideAuth({
          config: {
            authority: appConfig.oauthUrl,
            redirectUrl: `${window.location.origin}/callback`,
            clientId: appConfig.clientId,
            responseType: 'code',
            scope: 'openid profile email offline_access',
            postLogoutRedirectUri: `${window.location.origin}/${appConfig.origin}`,
            silentRenew: true,
            postLoginRoute: '/dashboard',
            forbiddenRoute: '/forbidden',
            unauthorizedRoute: '/unauthorized',
            logLevel: LogLevel.Error,
            useRefreshToken: true,
            secureRoutes: [appConfig.apiUrl],
            maxIdTokenIatOffsetAllowedInSeconds: 300,
          },
        }),
        { provide: AbstractSecurityStorage, useClass: LocalStorageService },
        // Custom auth initializer: detect state mismatch before checkAuth() to prevent
        // blocking the app. If the URL state doesn't match stored OIDC state, start a
        // fresh auth flow — Keycloak will instantly redirect if session is active.
        provideAppInitializer(() => {
          const oidcSecurityService = inject(OidcSecurityService);
          const urlState = new URL(window.location.toString()).searchParams.get('state');

          if (urlState) {
            return firstValueFrom(
              oidcSecurityService.getState().pipe(
                switchMap((storedState) => {
                  if (storedState !== urlState) {
                    oidcSecurityService.authorize();
                    return NEVER;
                  }
                  return oidcSecurityService.checkAuth();
                }),
                catchError(() => of({ isAuthenticated: false })),
              ),
            );
          }

          return firstValueFrom(
            oidcSecurityService.checkAuth().pipe(catchError(() => of({ isAuthenticated: false }))),
          );
        }),
      ],
    }).catch((err) => console.error(err));
  })
  .catch((err) => console.error('Failed to load app configuration:', err));
